import { useCallback, useEffect, useState } from 'react';

/**
 * Lazy artifact loader — fetches prompt/raw/variables from S3 on demand.
 * Resets when runId, stageId, or extractionId changes.
 */
export function useArtifact(
  runId: string | undefined,
  stageId: string,
  fetchFn: (runId: string, stageId: string, extractionId?: string) => Promise<string>,
  enabled: boolean,
  extractionId?: string,
): { data: string | null; loading: boolean; error: string | null; load: () => void } {
  const [data, setData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);

  // Reset when stage, run, or extractionId changes
  useEffect(() => {
    setData(null);
    setError(null);
    setRequested(false);
  }, [runId, stageId, extractionId]);

  useEffect(() => {
    if (!requested || !enabled || !runId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFn(runId, stageId, extractionId)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [requested, enabled, runId, stageId, extractionId, fetchFn]);

  const load = useCallback(() => setRequested(true), []);
  return { data, loading, error, load };
}
